// Location detail page, split by job the way the screen page is:
//
//   Service    — what the service is doing. Its connection, and every line it has
//              pushed, verbatim, with what became of each. Read-only, which is
//              the point: no fields here means the page's one form and one
//              save bar stay whole on the other tab.
//   Screens  — the location's screens (list + create + delete). Not settings:
//              each of its actions is its own request the moment it is taken.
//   Settings — what the service is given, as ONE form with one save bar (see
//              location-configure-form.tsx).
//   History  — the location's audit trail, stream changes included.
//
// The screen editor owns `/w/$workspaceId/screens/$screenId`.

import {
    Badge,
    Button,
    DataTable,
    type DataTableColumn,
    Dialog,
    Input,
    Spinner,
    Tabs,
    toast,
} from "@pej/kore/components";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { ApiError } from "@/api/error";
import { useLocations } from "@/api/locations";
import { useCreateScreen, useScreens } from "@/api/screens";
import type { Screen } from "@/api/types";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { pinnedActionsTable, rowActionsColumn } from "@/components/row-actions";
import { EntityHistory } from "@/features/audit/entity-history";
import { LocationConfigureForm } from "@/features/locations/location-configure-form";
import { PostingsCard } from "@/features/locations/postings-card";
import { ServiceStatusCard } from "@/features/locations/service-status-card";
import { ScreenDeleteConfirm } from "@/features/screens/screen-delete-confirm";
import { applyProblemDetails, Field, Form, useKoreForm } from "@/lib/forms";

export const Route = createFileRoute("/w/$workspaceId/locations/$locationId")({
    // TanStack Router keeps this route's component instance mounted across a
    // $locationId-only navigation (same matched route, only the param changes),
    // so without this, LocationConfigureForm/ScreensTab would keep the
    // PREVIOUS location's react-hook-form defaultValues in state — saving would
    // then silently overwrite the new location's record with the old location's
    // values. `remountDeps` is the router's own (not a hand-rolled `key`
    // prop) mechanism for forcing a full component remount on a dep change;
    // a manual `key` on the children was tried first and left a stale,
    // never-unmounted copy of the previous location's subtree behind (confirmed
    // via mount/unmount effect logging) — `remountDeps` remounts cleanly.
    remountDeps: ({ params }) => params.locationId,
    component: LocationDetailPage,
});

function LocationDetailPage() {
    const { workspaceId, locationId } = Route.useParams();
    const locationsQuery = useLocations(workspaceId);
    const location = locationsQuery.data?.find((s) => s.id === locationId);

    if (locationsQuery.isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!location) {
        return <p className="text-muted-foreground">This location couldn't be found.</p>;
    }

    return (
        <div className="flex max-w-4xl flex-col gap-4">
            <h1 className="text-xl font-semibold tracking-tight">{location.name}</h1>
            {/* Service first: this is where an Overview problem row lands, and
                whoever followed it came to find out what the service is doing,
                not to rename anything. */}
            <Tabs defaultValue="service">
                <Tabs.List>
                    <Tabs.Trigger value="service">Posting service</Tabs.Trigger>
                    <Tabs.Trigger value="screens">Screens</Tabs.Trigger>
                    <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
                    <Tabs.Trigger value="history">History</Tabs.Trigger>
                </Tabs.List>
                <Tabs.Content value="service" className="flex flex-col gap-4 pt-4">
                    <ServiceStatusCard location={location} />
                    <PostingsCard stream={location.stream} />
                </Tabs.Content>
                <Tabs.Content value="screens" className="flex flex-col gap-4 pt-4">
                    <ScreensTab workspaceId={workspaceId} locationId={locationId} streamId={location.stream.streamId} />
                </Tabs.Content>
                <Tabs.Content value="settings" className="flex flex-col gap-4 pt-4">
                    <LocationConfigureForm workspaceId={workspaceId} location={location} />
                </Tabs.Content>
                <Tabs.Content value="history" className="pt-4">
                    <EntityHistory targetType="Location" targetId={locationId} variant="tab" />
                </Tabs.Content>
            </Tabs>
        </div>
    );
}

const createScreenSchema = z.object({
    name: z.string().trim().min(1, "Name is required"),
});
type CreateScreenValues = z.infer<typeof createScreenSchema>;
const CREATE_SCREEN_DEFAULTS: CreateScreenValues = { name: "" };

function ScreensTab({
    workspaceId,
    locationId,
    streamId,
}: {
    workspaceId: string;
    locationId: string;
    streamId: string;
}) {
    const router = useRouter();
    const screensQuery = useScreens(locationId);

    const [createOpen, setCreateOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<Screen | null>(null);

    const createScreen = useCreateScreen(locationId);

    const form = useKoreForm({ schema: createScreenSchema, defaultValues: CREATE_SCREEN_DEFAULTS });

    function handleCreateOpenChange(next: boolean) {
        setCreateOpen(next);
        if (!next) form.reset(CREATE_SCREEN_DEFAULTS);
    }

    async function onCreateSubmit(values: CreateScreenValues) {
        try {
            await createScreen.mutateAsync({ name: values.name, streamId });
            toast.success("Screen created");
            handleCreateOpenChange(false);
        } catch (err) {
            if (err instanceof ApiError) {
                // Violations sit on their fields; anything else is a toast.
                const { onFields, unmapped } = applyProblemDetails(form, err);
                if (!onFields) toastMutationError("Create failed", err, { unmapped });
                return;
            }
            throw err;
        }
    }

    const columns: DataTableColumn<Screen>[] = [
        { key: "name", header: "Name", cell: (s) => s.name },
        {
            key: "active",
            header: "Active",
            cell: (s) => (
                <Badge tone={s.active ? "success" : "neutral"} dot>
                    {s.active ? "Active" : "Inactive"}
                </Badge>
            ),
        },
        rowActionsColumn(
            (s) => `Actions for ${s.name}`,
            (s) => [
                { id: "delete", label: "Delete", icon: Trash2, destructive: true, onSelect: () => setDeleteTarget(s) },
            ],
        ),
    ];

    return (
        <>
            {/* No Card or heading: the tab already names the list, and kore's
                DataTable is its own bordered surface. */}
            <div className="flex justify-end">
                <Dialog open={createOpen} onOpenChange={handleCreateOpenChange}>
                    <Dialog.Trigger
                        render={
                            <Button>
                                <Plus aria-hidden="true" />
                                Create screen
                            </Button>
                        }
                    />
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>New screen</Dialog.Title>
                            <Dialog.Description>
                                Screens carry their own filters, copy, and display URL.
                            </Dialog.Description>
                        </Dialog.Header>
                        <Form form={form} onSubmit={onCreateSubmit} className="flex flex-col gap-4">
                            <Field
                                name="name"
                                control={form.control}
                                label="Name"
                                required
                                render={({ field }) => <Input {...field} placeholder="Counter screen" />}
                            />
                            <Dialog.Footer>
                                <Dialog.Close render={<Button variant="outline">Cancel</Button>} />
                                <Button type="submit" disabled={createScreen.isPending}>
                                    {createScreen.isPending ? "Creating…" : "Create screen"}
                                </Button>
                            </Dialog.Footer>
                        </Form>
                    </Dialog.Content>
                </Dialog>
            </div>
            <DataTable
                className={pinnedActionsTable}
                density="compact"
                caption="Screens"
                columns={columns}
                rows={screensQuery.data ?? []}
                rowKey={(s) => s.id}
                state={screensQuery.isLoading ? "loading" : undefined}
                empty={{ headline: "No screens yet", body: "Create a screen to get a display URL for this location." }}
                onRowClick={(s) =>
                    void router.navigate({
                        to: "/w/$workspaceId/screens/$screenId",
                        params: { workspaceId, screenId: s.id },
                    })
                }
            />
            <ScreenDeleteConfirm
                workspaceId={workspaceId}
                screen={deleteTarget}
                onClose={() => setDeleteTarget(null)}
            />
        </>
    );
}
